(function installMonsterAssistantContract(global) {
  'use strict';

  var SCHEMA_VERSION = '2';
  var STATUS = Object.freeze({
    VERIFIED: 'verified_candidate',
    QUALIFIED: 'qualified_product_recommendation',
    CLARIFICATION: 'clarification_required',
    NO_CANDIDATE: 'no_verified_candidate',
    HANDOFF: 'specialist_handoff',
    PRODUCT_FACTS: 'product_facts_only'
  });
  var ALLOWED_STATUSES = new Set(Object.keys(STATUS).map(function statusValue(key) { return STATUS[key]; }));
  var ALLOWED_AUDIENCES = new Set(['guided', 'informed', 'technical']);
  var ALLOWED_ACTION_TYPES = new Set(['product', 'collection', 'quote', 'handoff']);
  var MONSTER_HOSTS = new Set(['www.monstermechanical.co.za', 'monstermechanical.co.za']);
  var PRODUCT_HANDLE = /^[a-z0-9][a-z0-9-]{0,127}$/;
  var BRAND = /^[A-Za-z0-9][A-Za-z0-9 .&+-]{1,39}$/;

  function text(value, limit) {
    return typeof value === 'string' ? value.trim().slice(0, limit) : '';
  }

  function token(value) {
    return text(value, 160).toUpperCase().replace(/[^A-Z0-9]+/g, '');
  }

  function finiteNumber(value) {
    var number = Number(value);
    return Number.isFinite(number) ? number : null;
  }

  function stringList(value, limit, itemLimit) {
    if (!Array.isArray(value)) return [];
    return value.slice(0, limit).map(function normalizeItem(item) { return text(item, itemLimit); }).filter(Boolean);
  }

  function canonicalMonsterUrl(value, kind, expectedHandle) {
    if (typeof value !== 'string' || !value.trim()) return null;
    try {
      var url = new URL(value.trim());
      if (url.protocol !== 'https:' || !MONSTER_HOSTS.has(url.hostname.toLowerCase())) return null;
      if (url.username || url.password || url.search || url.hash) return null;
      var path = url.pathname.replace(/\/+$/, '') || '/';
      var expectedPath = '';
      if (kind === 'product') {
        if (!PRODUCT_HANDLE.test(expectedHandle || '')) return null;
        expectedPath = '/products/' + expectedHandle;
      } else if (kind === 'collection') {
        if (!/^\/collections\/[a-z0-9][a-z0-9-]{0,127}$/.test(path)) return null;
        expectedPath = path;
      } else if (kind === 'quote') {
        expectedPath = '/pages/request-a-quote';
      } else if (kind === 'handoff') {
        if (path !== '/pages/request-a-quote' && path !== '/pages/contact') return null;
        expectedPath = path;
      } else {
        return null;
      }
      if (path !== expectedPath) return null;
      return 'https://www.monstermechanical.co.za' + expectedPath;
    } catch (error) {
      return null;
    }
  }

  function safeEvidenceUrl(value) {
    if (typeof value !== 'string' || !value.trim()) return '';
    try {
      var url = new URL(value.trim());
      if (url.protocol !== 'https:' || url.username || url.password) return '';
      return url.href.slice(0, 800);
    } catch (error) {
      return '';
    }
  }

  function normalizeExclusions(value) {
    var source = value && typeof value === 'object' && !Array.isArray(value) ? value : {};
    return {
      included_brands: stringList(source.included_brands, 8, 40).map(token).filter(Boolean),
      excluded_brands: stringList(source.excluded_brands, 8, 40).map(token).filter(Boolean),
      substitution_allowed: source.substitution_allowed === true
    };
  }

  function normalizeCandidate(value, exclusions, errors) {
    if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
    var catalogueKey = text(value.catalogue_key, 80);
    var brand = text(value.brand, 40);
    var model = text(value.model, 100);
    var title = text(value.title, 180);
    var handle = text(value.handle, 128).toLowerCase();
    var productId = finiteNumber(value.product_id);
    var variantId = finiteNumber(value.variant_id);
    var url = canonicalMonsterUrl(value.url, 'product', handle);
    var brandToken = token(brand);
    var modelToken = token(model);
    var titleToken = token(title);

    if (!catalogueKey) errors.push('candidate_catalogue_key_missing');
    if (!BRAND.test(brand)) errors.push('candidate_brand_invalid');
    if (!modelToken) errors.push('candidate_model_invalid');
    if (!titleToken || !titleToken.includes(modelToken)) errors.push('candidate_title_model_mismatch');
    if (!brandToken || !titleToken.includes(brandToken)) errors.push('candidate_title_brand_mismatch');
    if (!PRODUCT_HANDLE.test(handle)) errors.push('candidate_handle_invalid');
    if (!url) errors.push('candidate_url_handle_mismatch');
    if (!Number.isSafeInteger(productId) || productId <= 0) errors.push('candidate_product_id_invalid');
    if (!Number.isSafeInteger(variantId) || variantId <= 0) errors.push('candidate_variant_id_invalid');
    if (exclusions.excluded_brands.includes(brandToken)) errors.push('candidate_brand_excluded');
    if (exclusions.included_brands.length && !exclusions.included_brands.includes(brandToken)) errors.push('candidate_brand_not_included');

    return {
      catalogue_key: catalogueKey,
      brand: brand,
      model: model,
      product_id: productId,
      variant_id: variantId,
      title: title,
      handle: handle,
      url: url || '',
      variant_title: text(value.variant_title, 120),
      sku: text(value.sku, 80),
      price_zar: finiteNumber(value.price_zar),
      availability: ['available', 'unavailable', 'unknown'].includes(value.availability) ? value.availability : 'unknown',
      image: text(value.image, 800)
    };
  }

  function normalizeHydraulicCheck(value, candidate, errors) {
    if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
    var candidateKey = text(value.candidate_key, 80);
    var brand = text(value.brand, 40);
    var model = text(value.model, 100);
    var requiredHead = finiteNumber(value.required_head_m);
    var curveHead = finiteNumber(value.curve_head_m);
    var margin = finiteNumber(value.margin_m);
    if (!candidateKey || candidateKey !== candidate.catalogue_key) errors.push('hydraulic_candidate_key_mismatch');
    if (token(brand) !== token(candidate.brand)) errors.push('hydraulic_candidate_brand_mismatch');
    if (token(model) !== token(candidate.model)) errors.push('hydraulic_candidate_model_mismatch');
    if (requiredHead === null || requiredHead <= 0) errors.push('hydraulic_required_head_invalid');
    if (curveHead === null || curveHead < requiredHead) errors.push('hydraulic_curve_head_invalid');
    if (margin === null || Math.abs((curveHead - requiredHead) - margin) > 0.06) errors.push('hydraulic_margin_invalid');
    return {
      candidate_key: candidateKey,
      brand: brand,
      model: model,
      flow_m3h: finiteNumber(value.flow_m3h),
      required_head_m: requiredHead,
      curve_head_m: curveHead,
      margin_m: margin,
      method: text(value.method, 120),
      bounds: stringList(value.bounds, 4, 120),
      final_specification: false
    };
  }

  function normalizeEvidence(value, candidate, errors) {
    if (!Array.isArray(value)) return [];
    var evidence = value.slice(0, 8).map(function normalizeSource(source) {
      if (!source || typeof source !== 'object' || Array.isArray(source)) return null;
      return {
        source_id: text(source.source_id, 100),
        oem: text(source.oem, 40),
        document: text(source.document, 180),
        url: safeEvidenceUrl(source.url),
        verified_at: text(source.verified_at, 40)
      };
    }).filter(function completeSource(source) {
      return source && source.source_id && source.oem && source.document && source.url;
    });
    if (candidate && !evidence.some(function matchingOem(source) { return token(source.oem) === token(candidate.brand); })) {
      errors.push('candidate_oem_evidence_missing');
    }
    return evidence;
  }

  function normalizeActions(value, status, candidate, errors) {
    if (!Array.isArray(value)) return [];
    var actions = [];
    value.slice(0, 5).forEach(function normalizeAction(action) {
      if (!action || typeof action !== 'object' || Array.isArray(action)) return;
      var type = text(action.type, 20);
      var label = text(action.label, 80);
      if (!ALLOWED_ACTION_TYPES.has(type) || !label) {
        errors.push('action_invalid');
        return;
      }
      if (type === 'product') {
        if (!candidate || (status !== STATUS.VERIFIED && status !== STATUS.QUALIFIED && status !== STATUS.PRODUCT_FACTS)) {
          errors.push('product_action_without_candidate');
          return;
        }
        var productUrl = canonicalMonsterUrl(action.url, 'product', candidate.handle);
        if (!productUrl || productUrl !== candidate.url) {
          errors.push('product_action_candidate_mismatch');
          return;
        }
        actions.push({ type: type, label: label, url: productUrl });
        return;
      }
      var safeUrl = canonicalMonsterUrl(action.url, type);
      if (!safeUrl) {
        errors.push('action_url_invalid');
        return;
      }
      actions.push({ type: type, label: label, url: safeUrl });
    });
    return actions;
  }

  function normalizeDuty(value) {
    var source = value && typeof value === 'object' && !Array.isArray(value) ? value : {};
    return {
      application: text(source.application, 120),
      flow_m3h: finiteNumber(source.flow_m3h),
      tdh_m: finiteNumber(source.tdh_m),
      suction: text(source.suction, 120),
      voltage_v: finiteNumber(source.voltage_v),
      phase: text(source.phase, 40),
      fluid: text(source.fluid, 120),
      temp_c: finiteNumber(source.temp_c),
      assumptions: stringList(source.assumptions, 8, 180)
    };
  }

  function validateResponsePayload(payload) {
    var errors = [];
    if (!payload || typeof payload !== 'object' || Array.isArray(payload)) return { ok: false, errors: ['payload_invalid'], response: null };
    if (String(payload.schema_version || '') !== SCHEMA_VERSION) errors.push('schema_version_invalid');
    var status = text(payload.status, 40);
    if (!ALLOWED_STATUSES.has(status)) errors.push('status_invalid');
    var summary = text(payload.summary, 2400);
    if (!summary) errors.push('summary_missing');
    var audience = ALLOWED_AUDIENCES.has(payload.audience) ? payload.audience : 'guided';
    var exclusions = normalizeExclusions(payload.exclusions);
    var candidate = normalizeCandidate(payload.candidate, exclusions, errors);

    if ([STATUS.CLARIFICATION, STATUS.NO_CANDIDATE, STATUS.HANDOFF].includes(status) && candidate) {
      errors.push('candidate_forbidden_for_status');
    }
    if ([STATUS.VERIFIED, STATUS.QUALIFIED, STATUS.PRODUCT_FACTS].includes(status) && !candidate) {
      errors.push('candidate_required_for_status');
    }

    var hydraulicCheck = null;
    if (status === STATUS.VERIFIED && candidate) {
      hydraulicCheck = normalizeHydraulicCheck(payload.hydraulic_check, candidate, errors);
      if (!hydraulicCheck) errors.push('hydraulic_check_required');
    } else if (payload.hydraulic_check) {
      errors.push('hydraulic_check_forbidden_for_status');
    }

    var evidence = normalizeEvidence(payload.evidence, candidate, errors);
    if ([STATUS.VERIFIED, STATUS.QUALIFIED, STATUS.PRODUCT_FACTS].includes(status) && !evidence.length) errors.push('evidence_required');
    var actions = normalizeActions(payload.actions, status, candidate, errors);

    var response = {
      schema_version: SCHEMA_VERSION,
      response_id: text(payload.response_id || payload.request_id, 100),
      worker_version: text(payload.worker_version, 100),
      status: status,
      audience: audience,
      summary: summary,
      understood_duty: normalizeDuty(payload.understood_duty),
      candidate: candidate,
      hydraulic_check: hydraulicCheck,
      evidence: evidence,
      missing_facts: stringList(payload.missing_facts, 10, 180),
      exclusions: exclusions,
      unresolved_checks: stringList(payload.unresolved_checks, 10, 220),
      actions: actions,
      presentation: {
        headline: text(payload.presentation && payload.presentation.headline, 140),
        next_question: text(payload.presentation && payload.presentation.next_question, 360)
      }
    };
    return { ok: errors.length === 0, errors: errors, response: errors.length ? null : response };
  }

  global.MM_AI_RESPONSE_CONTRACT_V2 = Object.freeze({
    SCHEMA_VERSION: SCHEMA_VERSION,
    STATUS: STATUS,
    validateResponsePayload: validateResponsePayload
  });
})(typeof window !== 'undefined' ? window : globalThis);
